import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChartBar } from "./ChartBar";
import { useChartData } from "../hooks/useChartData";
import { useUsageData } from "../hooks/useUsageData";
import { USAGE_VIEW_TYPES } from "@/constants/usage";
import { isAuthHttpError } from "@/utils/httpError";
const ChartSkeleton = ({ viewType })=>/*#__PURE__*/ _jsx("div", {
        className: "flex items-end justify-between h-64 space-x-1",
        children: Array.from({
            length: viewType === USAGE_VIEW_TYPES.HOURLY ? 24 : 7
        }).map((_, i)=>/*#__PURE__*/ _jsx("div", {
                className: "flex-1",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-full h-20 bg-gray-200 rounded-t-sm animate-pulse"
                })
            }, i))
    });
const ChartError = ({ error })=>{
    const isAuthError = isAuthHttpError(error);
    return /*#__PURE__*/ _jsx("div", {
        className: "flex items-center justify-center h-64 text-center",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "text-gray-500",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-lg font-medium",
                    children: isAuthError ? "Usage Data Unavailable" : "Failed to load usage data"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm mt-2",
                    children: isAuthError ? "Authentication required to view usage analytics. Please ensure you have valid API keys and proper access." : error.message
                }),
                isAuthError && /*#__PURE__*/ _jsx("p", {
                    className: "text-xs mt-3 text-blue-600",
                    children: "Try creating an API key first, then refresh this page."
                })
            ]
        })
    });
};
const EmptyChart = ({ viewType })=>/*#__PURE__*/ _jsx("div", {
        className: "flex items-center justify-center h-64 text-center",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "text-gray-500",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-lg font-medium",
                    children: "No usage data available"
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "text-sm mt-2",
                    children: [
                        "No ",
                        viewType,
                        " usage recorded for the selected period"
                    ]
                })
            ]
        })
    });
export const UsageBarChart = ({ contractId, filters })=>{
    const { data, isLoading, error } = useUsageData(filters, contractId);
    const { chartData, maxValue, totalUsage, avgUsage, unit, isEmpty } = useChartData(data, filters);
    const title = filters.viewType === USAGE_VIEW_TYPES.HOURLY ? "Hourly Usage Pattern" : "Daily Usage Pattern";
    if (isLoading) return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white shadow rounded-lg",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-200",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: title
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-gray-500",
                                    children: "Loading usage data..."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded",
                            children: "UTC Time"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-6",
                children: /*#__PURE__*/ _jsx(ChartSkeleton, {
                    viewType: filters.viewType
                })
            })
        ]
    });
    if (error) return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white shadow rounded-lg",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-200",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: title
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-gray-500",
                                    children: [
                                        "Usage in ",
                                        unit
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded",
                            children: "UTC Time"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-6",
                children: /*#__PURE__*/ _jsx(ChartError, {
                    error: error
                })
            })
        ]
    });
    if (isEmpty) return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white shadow rounded-lg",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-200",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: title
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-sm text-gray-500",
                                    children: [
                                        "Usage in ",
                                        unit
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded",
                            children: "UTC Time"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "p-6",
                children: /*#__PURE__*/ _jsx(EmptyChart, {
                    viewType: filters.viewType
                })
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-white shadow rounded-lg",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "px-6 py-4 border-b border-gray-200",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-lg font-medium text-gray-900",
                                        children: title
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-sm text-gray-500",
                                        children: [
                                            "Usage in ",
                                            unit
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded",
                                children: "UTC Time"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4 mt-2 text-sm text-gray-600",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    "Total: ",
                                    totalUsage.toLocaleString(),
                                    " ",
                                    unit
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    "Average: ",
                                    avgUsage.toLocaleString(),
                                    " ",
                                    unit
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "p-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col justify-between h-64 mr-4 text-xs text-gray-500 shrink-0",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-right",
                                        children: maxValue.toLocaleString()
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-right",
                                        children: Math.round(maxValue * 0.75).toLocaleString()
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-right",
                                        children: Math.round(maxValue * 0.5).toLocaleString()
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-right",
                                        children: Math.round(maxValue * 0.25).toLocaleString()
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-right",
                                        children: "0"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex-1 overflow-x-auto min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "inline-flex items-end h-64 gap-1 border-l border-b border-gray-200",
                                        style: {
                                            minWidth: "100%"
                                        },
                                        children: chartData.map((dataPoint, index)=>/*#__PURE__*/ _jsx(ChartBar, {
                                                data: dataPoint,
                                                maxValue: maxValue
                                            }, `${dataPoint.label}-${index}`))
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-3 h-10",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: "inline-flex gap-1",
                                            style: {
                                                minWidth: "100%"
                                            },
                                            children: chartData.map((point, index)=>/*#__PURE__*/ _jsx("div", {
                                                    className: "text-center w-10 shrink-0 grow",
                                                    children: /*#__PURE__*/ _jsx("span", {
                                                        className: "text-xs text-gray-500 inline-block transform -rotate-45 origin-top whitespace-nowrap",
                                                        children: point.label
                                                    })
                                                }, index))
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-center mt-8",
                        children: /*#__PURE__*/ _jsx("span", {
                            className: "text-xs text-gray-500 font-medium",
                            children: filters.viewType === USAGE_VIEW_TYPES.HOURLY ? "Hour of Day" : "Date"
                        })
                    })
                ]
            })
        ]
    });
};
